import { MegaphoneIcon, SendIcon } from 'lucide-react-native';
import * as React from 'react';
import { View } from 'react-native';

import {
  fetchAdminBroadcasts,
  previewAdminBroadcast,
  sendAdminBroadcast,
} from '@/lib/admin-broadcast';
import type {
  BroadcastAudience,
  BroadcastChannel,
  BroadcastPreview,
  BroadcastWithRecipients,
} from '@sixam/shared/admin-broadcast-core';
import { Button } from '@sixam/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@sixam/ui/card';
import { Input } from '@sixam/ui/input';
import { Label } from '@sixam/ui/label';
import { Text } from '@sixam/ui/text';

type AdminBroadcastsScreenProps = Readonly<{
  searchQuery?: string;
  tenantSlug: string;
}>;

export function AdminBroadcastsScreen({ searchQuery, tenantSlug }: AdminBroadcastsScreenProps) {
  const [broadcasts, setBroadcasts] = React.useState<BroadcastWithRecipients[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let active = true;
    async function load() {
      setIsLoading(true);
      try {
        const data = await fetchAdminBroadcasts(tenantSlug);
        if (active) {
          setBroadcasts(data);
          setError(null);
        }
      } catch (loadError) {
        if (active) {
          setError(loadError instanceof Error ? loadError.message : 'Could not load broadcasts.');
        }
      } finally {
        if (active) {
          setIsLoading(false);
        }
      }
    }
    void load();
    return () => {
      active = false;
    };
  }, [tenantSlug]);

  return (
    <AdminBroadcastsPanel
      broadcasts={broadcasts}
      error={error}
      isLoading={isLoading}
      onBroadcastSent={(broadcast) => setBroadcasts((current) => [broadcast, ...current])}
      searchQuery={searchQuery}
      tenantSlug={tenantSlug}
    />
  );
}

export function AdminBroadcastsPanel({
  broadcasts,
  error,
  isLoading,
  onBroadcastSent,
  searchQuery,
  tenantSlug,
}: Readonly<{
  broadcasts: BroadcastWithRecipients[];
  error?: string | null;
  isLoading?: boolean;
  onBroadcastSent?: (broadcast: BroadcastWithRecipients) => void;
  searchQuery?: string;
  tenantSlug: string;
}>) {
  const [audience, setAudience] = React.useState<BroadcastAudience>('all_members');
  const [channels, setChannels] = React.useState<BroadcastChannel[]>(['push']);
  const [title, setTitle] = React.useState('');
  const [body, setBody] = React.useState('');
  const [eventId, setEventId] = React.useState('');
  const [preview, setPreview] = React.useState<BroadcastPreview | null>(null);
  const [isPreviewing, setIsPreviewing] = React.useState(false);
  const [isSending, setIsSending] = React.useState(false);
  const [formError, setFormError] = React.useState<string | null>(null);

  const draft = React.useMemo(
    () => ({
      audience,
      audienceFilter: { eventId: eventId.trim() || null },
      body,
      channels,
      title,
    }),
    [audience, body, channels, eventId, title]
  );

  function toggleChannel(channel: BroadcastChannel) {
    setChannels((current) =>
      current.includes(channel) ? current.filter((item) => item !== channel) : [...current, channel]
    );
  }

  async function previewDraft() {
    setIsPreviewing(true);
    setFormError(null);
    try {
      setPreview(await previewAdminBroadcast(tenantSlug, draft));
    } catch (previewError) {
      setFormError(
        previewError instanceof Error ? previewError.message : 'Could not preview broadcast.'
      );
    } finally {
      setIsPreviewing(false);
    }
  }

  async function sendDraft() {
    setIsSending(true);
    setFormError(null);
    try {
      const sent = await sendAdminBroadcast(tenantSlug, draft);
      onBroadcastSent?.(sent);
      setTitle('');
      setBody('');
      setPreview(null);
    } catch (sendError) {
      setFormError(sendError instanceof Error ? sendError.message : 'Could not send broadcast.');
    } finally {
      setIsSending(false);
    }
  }

  return (
    <View className="gap-5">
      <Text variant="muted">Send tenant-scoped updates to members and contact audiences.</Text>

      <Card>
        <CardHeader>
          <CardTitle>Compose</CardTitle>
        </CardHeader>
        <CardContent className="gap-5">
          <View className="gap-1.5">
            <Label>Audience</Label>
            <View className="flex-row flex-wrap gap-2">
              <ChoiceButton
                active={audience === 'all_members'}
                onPress={() => setAudience('all_members')}
              >
                Members
              </ChoiceButton>
              <ChoiceButton
                active={audience === 'contacts'}
                onPress={() => setAudience('contacts')}
              >
                Contacts
              </ChoiceButton>
              <ChoiceButton
                active={audience === 'event_registrants'}
                onPress={() => setAudience('event_registrants')}
              >
                Event
              </ChoiceButton>
            </View>
          </View>

          {audience === 'event_registrants' ? (
            <View className="gap-1.5">
              <Label>Event ID</Label>
              <Input
                autoCapitalize="none"
                onChangeText={setEventId}
                placeholder="Event UUID"
                value={eventId}
              />
            </View>
          ) : null}

          <View className="gap-1.5">
            <Label>Channels</Label>
            <View className="flex-row flex-wrap gap-2">
              <ChoiceButton
                active={channels.includes('push')}
                onPress={() => toggleChannel('push')}
              >
                Push
              </ChoiceButton>
              <ChoiceButton
                active={channels.includes('email')}
                onPress={() => toggleChannel('email')}
              >
                Email
              </ChoiceButton>
            </View>
          </View>

          <View className="gap-1.5">
            <Label>Title</Label>
            <Input onChangeText={setTitle} placeholder="Route change" value={title} />
          </View>

          <View className="gap-1.5">
            <Label>Body</Label>
            <Input
              className="h-28 py-2"
              multiline
              numberOfLines={5}
              onChangeText={setBody}
              placeholder="Short captain update"
              value={body}
            />
          </View>

          {preview ? <BroadcastPreviewSummary preview={preview} /> : null}
          {formError ? (
            <Text className="text-sm font-medium text-destructive">{formError}</Text>
          ) : null}

          <View className="gap-2 sm:flex-row">
            <Button
              className="sm:flex-1"
              disabled={isPreviewing || isSending}
              onPress={previewDraft}
              variant="outline"
            >
              <MegaphoneIcon color="white" size={18} />
              <Text>{isPreviewing ? 'Previewing...' : 'Preview'}</Text>
            </Button>
            <Button className="sm:flex-1" disabled={isPreviewing || isSending} onPress={sendDraft}>
              <SendIcon color="white" size={18} />
              <Text>{isSending ? 'Sending...' : 'Send now'}</Text>
            </Button>
          </View>
        </CardContent>
      </Card>

      <RecentBroadcasts
        broadcasts={broadcasts}
        error={error}
        isLoading={isLoading}
        searchQuery={searchQuery}
      />
    </View>
  );
}

function ChoiceButton({
  active,
  children,
  onPress,
}: Readonly<{ active: boolean; children: string; onPress: () => void }>) {
  return (
    <Button onPress={onPress} size="sm" variant={active ? 'default' : 'outline'}>
      <Text>{children}</Text>
    </Button>
  );
}

function BroadcastPreviewSummary({ preview }: Readonly<{ preview: BroadcastPreview }>) {
  return (
    <View className="flex-row flex-wrap gap-3 rounded-md border border-border p-4">
      <Metric label="Recipients" value={preview.recipientCount} />
      <Metric label="Push" value={preview.pushCount} />
      <Metric label="Email" value={preview.emailCount} />
      <Metric label="Skipped" value={preview.skippedCount} />
      {!preview.emailConfigured && preview.emailCount > 0 ? (
        <Text className="basis-full text-sm text-muted-foreground">
          Email provider is not configured; email recipients will be skipped.
        </Text>
      ) : null}
    </View>
  );
}

function RecentBroadcasts({
  broadcasts,
  error,
  isLoading,
  searchQuery,
}: Readonly<{
  broadcasts: BroadcastWithRecipients[];
  error?: string | null;
  isLoading?: boolean;
  searchQuery?: string;
}>) {
  const normalizedSearchQuery = searchQuery?.trim().toLowerCase() ?? '';
  const visibleBroadcasts = normalizedSearchQuery
    ? broadcasts.filter((broadcast) =>
        [broadcast.title, broadcast.body, broadcast.status].some((value) =>
          value.toLowerCase().includes(normalizedSearchQuery)
        )
      )
    : broadcasts;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Recent</CardTitle>
      </CardHeader>
      <CardContent className="gap-3">
        {isLoading ? <Text variant="mono">Loading broadcasts...</Text> : null}
        {error ? <Text className="text-sm font-medium text-destructive">{error}</Text> : null}
        {!isLoading && visibleBroadcasts.length === 0 ? (
          <Text variant="muted">No broadcasts sent yet.</Text>
        ) : null}
        {visibleBroadcasts.map((broadcast) => (
          <View key={broadcast.id} className="gap-2 rounded-md border border-border p-4">
            <View className="gap-1 sm:flex-row sm:items-start sm:justify-between">
              <View className="min-w-0 flex-1 gap-1">
                <Text variant="large" numberOfLines={1}>
                  {broadcast.title}
                </Text>
                <Text variant="muted" numberOfLines={2}>
                  {broadcast.body}
                </Text>
              </View>
              <Text variant="label" className="text-muted-foreground">
                {broadcast.status.replace('_', ' ')}
              </Text>
            </View>
            <View className="flex-row flex-wrap gap-3">
              <Metric label="Sent" value={broadcast.sent_count} />
              <Metric label="Failed" value={broadcast.failed_count} />
              <Metric label="Skipped" value={broadcast.skipped_count} />
            </View>
            {broadcast.last_error ? (
              <Text className="text-sm text-destructive">{broadcast.last_error}</Text>
            ) : null}
          </View>
        ))}
      </CardContent>
    </Card>
  );
}

function Metric({ label, value }: Readonly<{ label: string; value: number }>) {
  return (
    <View className="min-w-20 gap-1">
      <Text variant="label" className="text-muted-foreground">
        {label}
      </Text>
      <Text variant="mono">{value}</Text>
    </View>
  );
}
